import React, { useEffect, useState } from "react";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { OnboardingFlow } from "@/components/onboarding/OnboardingFlow";
import { OnboardingErrorBoundary } from "@/components/onboarding/OnboardingErrorBoundary";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FuturisticLoader } from "@/components/ui/FuturisticLoader";
import { industryService } from "@/services/industryService";
import { welcomeFormService } from "@/services/welcomeFormService";

export default function OnboardingPage() {
  const { user, organization, isLoading, refreshProfile } = useAuth();
  const location = useLocation();
  const isNewUser = location.state?.isNewUser;
  const [retryCount, setRetryCount] = useState(0);
  const [showError, setShowError] = useState(false);

  // Add states to preload all required data
  const [industriesLoaded, setIndustriesLoaded] = useState(false);
  const [welcomeFormLoaded, setWelcomeFormLoaded] = useState(false);
  const [preloadedIndustries, setPreloadedIndustries] = useState<any[]>([]);
  const [allDataReady, setAllDataReady] = useState(false);

  // If coming from registration, show a seamless loading transition
  useEffect(() => {
    if (isNewUser && user && organization) {
      // Small delay to ensure smooth transition from registration loading
      const timer = setTimeout(() => {
        // Clear the registration state to prevent issues on refresh
        window.history.replaceState({}, document.title);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isNewUser, user, organization]);

  // Retry logic for organization loading - but don't retry during onboarding flow
  useEffect(() => {
    // Skip retry logic if we're in active onboarding
    const inOnboardingFlow =
      sessionStorage.getItem("newUserOnboarding") === "true" ||
      sessionStorage.getItem("pendingNewUserRedirect") === "true";

    if (inOnboardingFlow) {
      return;
    }

    if (user && !organization && !isLoading && retryCount < 5) {
      const timer = setTimeout(
        () => {
          setRetryCount((prev) => prev + 1);
          // Trigger a refresh of the profile data
          refreshProfile();
        },
        1000 * Math.min(retryCount + 1, 3),
      ); // Exponential backoff, max 3 seconds

      return () => clearTimeout(timer);
    }

    // Show error after 5 retries
    if (user && !organization && !isLoading && retryCount >= 5) {
      console.error(
        "[OnboardingPage] Failed to load organization after 5 retries",
      );
      setShowError(true);
    }
  }, [user, organization, isLoading, retryCount, refreshProfile]);

  // Preload industries data to prevent reload in ModernOnboardingFlow
  useEffect(() => {
    const preloadIndustries = async () => {
      try {
        const { data, error } = await industryService.getAllIndustries();
        if (error) {
          console.error("Failed to preload industries:", error);
        } else {
          setPreloadedIndustries(data || []);
          // Store in sessionStorage so ModernOnboardingFlow can access immediately
          sessionStorage.setItem(
            "preloadedIndustries",
            JSON.stringify(data || []),
          );
        }
      } catch (error) {
        console.error("Error preloading industries:", error);
      } finally {
        setIndustriesLoaded(true);
      }
    };

    preloadIndustries();
  }, []);

  // Preload welcome form data to prevent reload in ModernOnboardingFlow
  useEffect(() => {
    const preloadWelcomeForm = async () => {
      if (!user?.id || !organization?.id) {
        return; // Wait for user and organization to be available
      }

      try {
        const welcomeResult = await welcomeFormService.loadWelcomeFormData(
          user.id,
          organization.id,
        );

        if (welcomeResult.success && welcomeResult.data) {
          // Store in sessionStorage so ModernOnboardingFlow can access immediately
          sessionStorage.setItem(
            "preloadedWelcomeForm",
            JSON.stringify(welcomeResult.data),
          );
        } else {
          sessionStorage.setItem("preloadedWelcomeForm", JSON.stringify(null));
        }
      } catch (error) {
        console.error("Error preloading welcome form:", error);
        sessionStorage.setItem("preloadedWelcomeForm", JSON.stringify(null));
      } finally {
        setWelcomeFormLoaded(true);
      }
    };

    preloadWelcomeForm();
  }, [user?.id, organization?.id]);

  // Check if all required data is ready
  useEffect(() => {
    const dataReady =
      !isLoading &&
      user &&
      organization &&
      industriesLoaded &&
      welcomeFormLoaded;

    if (dataReady && !allDataReady) {
      setAllDataReady(true);
    }
  }, [
    isLoading,
    user,
    organization,
    industriesLoaded,
    welcomeFormLoaded,
    allDataReady,
  ]);

  // Show loading while checking auth state OR waiting for all data
  if (isLoading || !allDataReady) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

        {/* Progress indicator */}
        <div className="w-full h-1 bg-gray-100 relative z-10">
          <div className="h-full bg-[#ecb22d] w-full transition-all duration-1000"></div>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-4 relative z-10 min-h-screen">
          <div className="max-w-2xl w-full text-center">
            <div className="flex justify-center mb-8">
              <img
                src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg`}
                alt="Hubflo Agents Logo"
                className="h-[50px] w-auto"
              />
            </div>

            <div
              className="bg-white p-6 rounded-2xl border border-gray-100 max-w-md mx-auto"
              style={{ boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)" }}
            >
              <p className="text-gray-800 text-base mb-6">
                {isNewUser
                  ? "Preparing your onboarding experience..."
                  : "Loading..."}
                {!industriesLoaded && " Loading industries..."}
                {industriesLoaded &&
                  !welcomeFormLoaded &&
                  " Loading profile..."}
                {industriesLoaded &&
                  welcomeFormLoaded &&
                  !allDataReady &&
                  " Almost ready..."}
              </p>

              <div className="flex justify-center">
                <FuturisticLoader size={60} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show error state with retry option
  if (showError) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

        <div className="flex-1 flex flex-col justify-center items-center px-4 relative z-10 min-h-screen">
          <div className="max-w-2xl w-full text-center">
            <div className="flex justify-center mb-8">
              <img
                src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg`}
                alt="Hubflo Agents Logo"
                className="h-[50px] w-auto"
              />
            </div>

            <div
              className="bg-white p-6 rounded-2xl border border-gray-100 max-w-md mx-auto"
              style={{ boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)" }}
            >
              <p className="text-gray-800 text-base mb-6">
                Unable to load your organization data. This might be a temporary
                issue.
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setShowError(false);
                    setRetryCount(0);
                    refreshProfile();
                  }}
                  className="w-full bg-[#ecb22d] hover:bg-[#d9a429] text-white"
                >
                  Try Again
                </Button>

                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Refresh Page
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no organization, show loading with retry info (this shouldn't happen after registration)
  if (!organization) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

        {/* Progress indicator */}
        <div className="w-full h-1 bg-gray-100 relative z-10">
          <div className="h-full bg-[#ecb22d] w-full transition-all duration-1000"></div>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center px-4 relative z-10 min-h-screen">
          <div className="max-w-2xl w-full text-center">
            <div className="flex justify-center mb-8">
              <img
                src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/hubflo%20agents%20horizontal%20dark.svg`}
                alt="Hubflo Agents Logo"
                className="h-[50px] w-auto"
              />
            </div>

            <div
              className="bg-white p-6 rounded-2xl border border-gray-100 max-w-md mx-auto"
              style={{ boxShadow: "1px 1px 3px rgba(0, 0, 0, 0.08)" }}
            >
              <p className="text-gray-800 text-base mb-4">
                Loading organization data...
              </p>

              {retryCount > 0 && (
                <p className="text-gray-600 text-sm mb-6">
                  Retry attempt {retryCount}/5
                </p>
              )}

              <div className="flex justify-center">
                <FuturisticLoader size={60} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Always show the onboarding flow with error boundary protection
  return (
    <OnboardingErrorBoundary>
      <OnboardingProvider>
        <OnboardingFlow />
      </OnboardingProvider>
    </OnboardingErrorBoundary>
  );
}
